/**
 * ZamanatBrainPage
 * =================
 * Main page for the Zamanat Brain module — unified knowledge management
 * with KB entries tab, system prompts tab, pending drafts banner, and AI chat.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { getPendingDrafts } from '../api/brain';
import { useOrg } from '../hooks';
import BrainKBEntriesTab from '../components/brain/BrainKBEntriesTab';
import BrainInternalKnowledgeTab from '../components/brain/BrainInternalKnowledgeTab';
import BrainDocumentsTab from '../components/brain/BrainDocumentsTab';
import PendingDraftsBanner from '../components/brain/PendingDraftsBanner';

const TABS = [
  { id: 'entities', label: 'Structure Entities' },
  { id: 'knowledge', label: 'Internal Knowledge' },
  { id: 'documents', label: 'Documents' },
];

const DEFAULT_TAB = 'entities';

const ZamanatBrainPage = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = TABS.find(t => t.id === tab)?.id || DEFAULT_TAB;

  const setActiveTab = (tabId) => {
    navigate(`/brain/${tabId}`, { replace: true });
  };
  const [pendingCount, setPendingCount] = useState(0);
  const { activeOrg } = useOrg();

  const fetchPendingDrafts = useCallback(async () => {
    try {
      // Only the total is needed for the banner, so fetch a single row
      // (limit=1) — the response still carries the unbounded `total`. This
      // avoids materializing up to 50 drafts plus their per-row requester
      // lookups on the backend, matching how the sidebar badge fetches.
      const res = await getPendingDrafts('pending', null, 1, 0);
      setPendingCount(res.data?.total ?? 0);
    } catch {
      // Silent fail — banner just won't show
    }
  // Refetch when the active org changes so the banner reflects the current org
  }, [activeOrg?.id]);

  useEffect(() => {
    fetchPendingDrafts();
  }, [fetchPendingDrafts]);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-1">
              {activeOrg?.name || 'Zamanat'} Brain
            </h1>
            <p className="text-gray-600 text-sm">
              AI knowledge management — structure entities, internal knowledge, and intelligent updates
            </p>
          </div>
        </div>

        {/* Pending Drafts Banner */}
        <PendingDraftsBanner
          count={pendingCount}
          onRefresh={fetchPendingDrafts}
        />

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-6 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium whitespace-nowrap flex-shrink-0 transition-colors border-b ${
                  activeTab === tab.id
                    ? 'text-primary border-primary'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'entities' && <BrainKBEntriesTab />}
        {activeTab === 'knowledge' && <BrainInternalKnowledgeTab />}
        {activeTab === 'documents' && <BrainDocumentsTab />}
      </div>

    </DashboardLayout>
  );
};

export default ZamanatBrainPage;
